// 📁 src/pages/BacktestsPage.tsx
// ================================================================================
// BLOKAS 1: BACKTESTSPAGE.TSX: FAILO INICIJAVIMAS IR IMPORTŲ KONFIGŪRACIJA
// ================================================================================
console.log('📁 [BLOKAS 1 BacktestsPage.tsx] Failas įkraunamas - TypeScript/React aplinka');

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, ChevronDown, RefreshCw, Plus, TrendingUp, BarChart3 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

import { DashboardLayout } from '../components/layouts/DashboardLayout';
import { Button } from '../ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { useBots } from '@/contexts/BotsContext';
import { supabase } from '@/lib/supabaseClient';
import BacktestCard from '../components/BacktestCard';
import { toast } from 'sonner';
import { pollBacktestCompletion } from '@/features/backtests/runBacktest';

export const BacktestsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { clearCurrentBot } = useBots();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'running' | 'queued' | 'failed'>('all');
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkBacktestStatus = React.useCallback(async (backtestId: string, backtestData: any) => {
    const currentTime = Date.now();
    const createdAtTime = new Date(backtestData.created_at).getTime();
    if (backtestData.status === 'running' && (currentTime - createdAtTime) > 30000) {
      try {
        const { data: resultData } = await supabase.from('backtest_results').select('error, status').eq('id', backtestId).single();
        if (resultData?.error) {
          toast.warning(`Backtest nutrauktas dėl klaidos`);
          setData(prev => prev.map(bt => bt.id === backtestId ? { ...bt, status: 'error', error: resultData.error } : bt));
        }
      } catch (error) {}
    }
  }, []);

  const checkAllBacktestsStatus = React.useCallback(async () => {
    const runningBacktests = data.filter(bt => bt.status === 'running' || bt.status === 'queued');
    if (runningBacktests.length === 0) return;
    for (const bt of runningBacktests) {
      await checkBacktestStatus(bt.id, bt);
    }
  }, [data, checkBacktestStatus]);

  const fetchBacktests = React.useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const { data: backtestsData, error: backtestsError } = await supabase
        .from('v_backtests_cards_v3')
        .select('*')
        .eq('user_id', user.id)
        .neq('status', 'deleted')
        .order('created_at', { ascending: false });

      if (backtestsError) throw backtestsError;

      if (backtestsData) {
        // 🔥 ANTI-DUBLIKATŲ LOGIKA: Išsaugome tik po 1 unikalų ID
        const uniqueDataMap = new Map();
        backtestsData.forEach(item => {
            if (!uniqueDataMap.has(item.id)) {
                uniqueDataMap.set(item.id, item);
            }
        });
        const uniqueData = Array.from(uniqueDataMap.values());
        
        setData(uniqueData);
        for (const bt of uniqueData) {
          if (bt.status === 'running' || bt.status === 'queued') {
            await checkBacktestStatus(bt.id, bt);
          }
        }
      } else {
        setData([]);
      }
    } catch (error: any) {
      setError(error.message);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, checkBacktestStatus]);

  const refreshBacktests = React.useCallback(async () => {
    toast.info('Atnaujinami backtestų duomenys...');
    await fetchBacktests();
    toast.success('Duomenys atnaujinti');
  }, [fetchBacktests]);

  const handleView = (id: string): void => {
    navigate('/backtest/' + id);
  };

  const handleEditBot = async (botId: string) => {
    if (!botId) return toast.error('Bot ID nerastas šiam backtestui');
    navigate(`/bots/${botId}/edit`);
  };

  // 🔥 TIKRAS IŠTRINIMAS IŠ DB APEINANT KLAIDINGĄ RPC API
  const handleDelete = async (id: string) => {
    console.log(`🔪 handleDelete iškviesta su ID: ${id}`);
    
    // Iškart pašaliname kortelę iš matomumo UI
    setData((prevData) => prevData.filter((bt) => bt.id !== id));
    
    try {
      // Vykdome fizinį trynimą
      const { error } = await supabase.from('backtest_results').delete().eq('id', id);
      
      if (error) {
          console.error('Klaida trinant iš DB (fallback į soft delete):', error);
          await supabase.from('backtest_results').update({ status: 'deleted', deleted_at: new Date().toISOString() }).eq('id', id);
      } else {
          toast.success('Backtestas sėkmingai ištrintas');
      }
    } catch (e) {
      console.error('Kritinė klaida trinant:', e);
      fetchBacktests();
    }
  };

  const handleCreateNewBot = () => {
    clearCurrentBot();
    navigate('/create-bot');
  };

  const filteredData = data.filter(item => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = (item.title || '').toLowerCase().includes(searchLower) || (item.bot_name || '').toLowerCase().includes(searchLower);
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalBacktests = data.length;
  const completedBacktests = data.filter(b => b.status === 'completed').length;
  const runningBacktests = data.filter(b => b.status === 'running' || b.status === 'queued').length;

  useEffect(() => { fetchBacktests(); }, [fetchBacktests]);

  useEffect(() => {
    if (!user?.id) return;
    const activeBacktests = data.filter(b => b.status === 'running' || b.status === 'queued');
    if (activeBacktests.length === 0) return;
    const interval = setInterval(() => checkAllBacktestsStatus(), 15000);
    return () => clearInterval(interval);
  }, [user?.id, data, checkAllBacktestsStatus]);

  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase.channel('backtests-page-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'backtest_results', filter: `user_id=eq.${user.id}` },
        (payload) => {
          const newRecord = payload.new as any;
          if (newRecord?.deleted_at || payload.eventType === 'DELETE') {
            const deleteId = newRecord?.id || payload.old?.id;
            setData(prev => prev.filter(bt => bt.id !== deleteId));
            return;
          }
          setTimeout(() => { refreshBacktests(); }, 2000);
        }
      ).subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id, refreshBacktests]);

  useEffect(() => {
    const justLaunchedId = (location.state as any)?.justLaunchedBacktestId;
    if (!justLaunchedId) return;
    let isCancelled = false;
    const waitCompletion = async () => {
      try {
        await pollBacktestCompletion(justLaunchedId, { timeoutMs: 180000, intervalMs: 1500 });
        if (!isCancelled) {
          await fetchBacktests();
          navigate(location.pathname, { replace: true, state: {} });
        }
      } catch (e) {
        if (!isCancelled) {
          await fetchBacktests();
          navigate(location.pathname, { replace: true, state: {} });
        }
      }
    };
    waitCompletion();
    return () => { isCancelled = true; };
  }, [location.state, navigate, location.pathname, fetchBacktests]);

  useEffect(() => {
    const handleBacktestCompleted = () => {
      setTimeout(() => { refreshBacktests(); }, 2000);
    };
    window.addEventListener('backtestCompleted', handleBacktestCompleted);
    return () => { window.removeEventListener('backtestCompleted', handleBacktestCompleted); };
  }, [refreshBacktests]);

  useEffect(() => {
    const active = data.some(bt => bt.status === 'running' || bt.status === 'queued');
    if (!active || loading) return;
    const interval = setInterval(() => { refreshBacktests(); }, 30000);
    return () => clearInterval(interval);
  }, [data, loading, refreshBacktests]);

  console.log('🎨 [BLOKAS 8 BacktestsPage.tsx] Pradedamas komponento renderinimas');

  return (
    <DashboardLayout>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} className="space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Backtestai</h1>
            <p className="text-gray-600 dark:text-gray-400">Peržiūrėkite ir valdykite savo prekybos strategijų backtestus</p>
          </div>
          <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
            <Button variant="primary" icon={<Plus size={16} />} iconPosition="left" onClick={handleCreateNewBot}>
              Sukurti naują botą
            </Button>
            <button onClick={refreshBacktests} disabled={loading} className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Atnaujinti
            </button>
            <div className="relative">
              <input type="text" placeholder="Ieškoti backtestų..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full md:w-64 pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
            <div className="relative">
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)} className="w-full md:w-40 pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none">
                <option value="all">Visi statusai</option>
                <option value="completed">Užbaigti</option>
                <option value="running">Vykdomi</option>
                <option value="queued">Eilėje</option>
                <option value="failed">Nepavyko</option>
              </select>
              <Filter className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <ChevronDown className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Visų backtestų</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalBacktests}</p>
              </div>
              <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-blue-500" />
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Užbaigta</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{completedBacktests}</p>
              </div>
              <div className="h-12 w-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-green-500" />
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Vykdoma</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{runningBacktests}</p>
              </div>
              <div className="h-12 w-12 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500"></div>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-lg border border-red-200 dark:border-red-800">
            <p className="text-red-800 dark:text-red-200">{error}</p>
            <button onClick={refreshBacktests} className="mt-2 text-red-600 dark:text-red-400 hover:text-red-800 font-medium">
              Bandyti dar kartą
            </button>
          </div>
        ) : filteredData.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredData.map((row) => (
              <BacktestCard
                key={row.id}
                row={row}
                onView={handleView}
                onEdit={handleEditBot}
                onDelete={handleDelete}
                onRefresh={refreshBacktests}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center shadow-md border border-gray-200 dark:border-gray-700">
            <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Backtestų nerasta</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Sukurkite pirmąjį botą ir paleiskite backtestą, kad čia matytumėte rezultatus
            </p>
            <Button variant="primary" onClick={handleCreateNewBot} icon={<Plus size={16} />} iconPosition="left">
              Sukurti naują botą
            </Button>
          </div>
        )}
      </motion.div>
    </DashboardLayout>
  );
};

export default BacktestsPage;
// ================================================================================
// BLOKAS 9: BACKTESTSPAGE.TSX: FAILO PABAIGOS DIAGNOSTIKA IR UŽDARYMAS
// ================================================================================
console.log('🏁 [BLOKAS 9 BacktestsPage.tsx] BacktestsPage.tsx failas sėkmingai apdorotas ir uždarytas');